<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import dayjs from 'dayjs';
import { AxiosError } from 'axios';
import { toast } from 'vue-sonner';
import { ArrowPathIcon, CheckIcon, ChevronDownIcon } from '@heroicons/vue/24/solid';
import { Button } from '@/components/ui/button';
import { LazyTooltip } from '@/components/ui/tooltip';
import ConfirmDialog from '@/components/ConfirmDialog.vue';
import ScheduleTypeIcon from '@/components/schedules/ScheduleTypeIcon.vue';
import LzpScheduleProgressBar from '@/components/DataTable/LzpScheduleProgressBar.vue';
import { useNowTicker } from '@/composables/useNowTicker';
import { useOpenCurrentPlaylist } from '@/composables/useOpenCurrentPlaylist';
import { useOpenRecordInInspector } from '@/composables/useOpenRecordInInspector';
import {
    useGenerateDailyListsMutation,
    useLocationZonePlayerSchedule,
    useReloadCustomListsMutation,
    type PlayerScheduleFile,
    type PlayerScheduleRow,
    type PlayerScheduleRun,
} from '@/composables/api/useLocationZonePlayerScheduleApi';
import { describeChange, formatSyncTime } from '@/utils/syncStatus';
import { DAY_TEXT_LABELS, SCHEDULE_TYPE, scheduleKindColor, type ScheduleKind } from '@/utils/customPlaylistSchedule';
import {
    dailyRangeSummary,
    dailyReasonText,
    deriveScheduleProgress,
    formatDailyDate,
    scheduleStatusLine,
    scheduleWhenText,
} from '@/utils/scheduleProgress';

/**
 * The player inspector's Schedule tab: the custom list schedules and daily
 * lists the player last reported, whether they are out of date and why, and
 * the two actions that reload them.
 *
 * Everything comes from GET /api/location_zone_player/{id}/schedule, the run in
 * progress included, so a page reload, a second browser tab and a second person
 * all see the same bar. The only state kept here is the last run seen, so a run
 * the server stops returning without a new report can still say "No answer from
 * the player since 2:14 PM".
 */
const props = defineProps<{
    locationZonePlayerId: number | null;
    /** The player's live row, for its last check-in time. */
    player: Record<string, unknown>;
}>();

const playerIdRef = computed(() => props.locationZonePlayerId);
const scheduleQuery = useLocationZonePlayerSchedule(playerIdRef);
const schedule = computed(() => scheduleQuery.data.value ?? null);
const nowMs = useNowTicker();

const errorMessage = computed(() => {
    const error = scheduleQuery.error.value;
    if (error instanceof AxiosError) {
        const message = (error.response?.data as { message?: unknown } | undefined)?.message;
        if (typeof message === 'string' && message.trim() !== '') {
            return message;
        }
    }
    return null;
});

const lastCheckinAt = computed(() => {
    const value = props.player.player_data_updated_at;
    return typeof value === 'string' && value !== '' ? value : null;
});

// ---------------------------------------------------------------------------
// The run in progress
// ---------------------------------------------------------------------------

/** The last run the server returned for this player. */
const lastSeenRun = ref<{ locationZonePlayerId: number | null; run: PlayerScheduleRun } | null>(null);

function reportIsNewerThan(run: PlayerScheduleRun): boolean {
    const reportedAt = schedule.value?.report.reported_at;
    return Boolean(reportedAt) && new Date(reportedAt as string).getTime() > new Date(run.started_at).getTime();
}

watch(schedule, (next) => {
    if (next?.run) {
        lastSeenRun.value = { locationZonePlayerId: props.locationZonePlayerId, run: next.run };
    } else if (lastSeenRun.value && reportIsNewerThan(lastSeenRun.value.run)) {
        lastSeenRun.value = null;
    }
});

watch(playerIdRef, () => {
    lastSeenRun.value = null;
    actionNotice.value = null;
});

const progress = computed(() => {
    const current = schedule.value;
    if (!current) {
        return null;
    }
    if (current.run) {
        return deriveScheduleProgress(current, nowMs.value, { lastCheckinAt: lastCheckinAt.value });
    }
    // The server stopped returning a run without a newer report: it timed out.
    const remembered = lastSeenRun.value;
    if (remembered && remembered.locationZonePlayerId === props.locationZonePlayerId && !reportIsNewerThan(remembered.run)) {
        return deriveScheduleProgress({ report: current.report, run: remembered.run }, nowMs.value, {
            serverEnded: true,
        });
    }
    return null;
});

const isRunning = computed(() => progress.value?.running === true);

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

const reloadMutation = useReloadCustomListsMutation(playerIdRef);
const generateMutation = useGenerateDailyListsMutation(playerIdRef);

/** The command issuer's reason for skipping, shown in place of the bar. */
const actionNotice = ref<{ kind: 'reload' | 'generate'; text: string } | null>(null);

const isReloading = computed(
    () => reloadMutation.isPending.value || (isRunning.value && progress.value?.kind === 'reload')
);
const isGenerating = computed(
    () => generateMutation.isPending.value || (isRunning.value && progress.value?.kind === 'generate')
);

const confirmReloadOpen = ref(false);
const confirmGenerateOpen = ref(false);

const generateMessage = computed(() => {
    const count = schedule.value?.zone_player_count ?? 0;
    return `This builds new daily lists for this zone and sends them to its ${count} ${count === 1 ? 'player' : 'players'}.`;
});

function requestReload(): void {
    if (!isReloading.value) {
        confirmReloadOpen.value = true;
    }
}

function requestGenerate(): void {
    if (!isGenerating.value) {
        confirmGenerateOpen.value = true;
    }
}

function serverMessage(error: unknown): string | undefined {
    if (error instanceof AxiosError) {
        if (error.response?.status === 403) {
            return 'You may not have permission to send commands to this player.';
        }
        const message = (error.response?.data as { message?: unknown } | undefined)?.message;
        return typeof message === 'string' && message.trim() !== '' ? message : undefined;
    }
    return undefined;
}

function reload(): void {
    lastSeenRun.value = null;
    actionNotice.value = null;
    reloadMutation.mutate(undefined, {
        onSuccess: (result) => {
            const skipped = result.skipped.find((entry) => entry.location_zone_player_id === props.locationZonePlayerId);
            if (skipped) {
                actionNotice.value = { kind: 'reload', text: `Not sent: ${skipped.reason}` };
            }
        },
        onError: (error) => {
            toast.error("Couldn't reload the custom lists", { description: serverMessage(error) });
        },
    });
}

function generate(): void {
    lastSeenRun.value = null;
    actionNotice.value = null;
    generateMutation.mutate(undefined, {
        onError: (error) => {
            toast.error("Couldn't generate the default lists", { description: serverMessage(error) });
        },
    });
}

// ---------------------------------------------------------------------------
// Status line and reasons
// ---------------------------------------------------------------------------

const reasonsOpen = ref(false);
const statusLine = computed(() => (schedule.value ? scheduleStatusLine(schedule.value, isRunning.value) : null));

// ---------------------------------------------------------------------------
// Custom list schedules
// ---------------------------------------------------------------------------

const { openRecord } = useOpenRecordInInspector();
const { openPlaylist } = useOpenCurrentPlaylist(playerIdRef);

/** The report carries no silence flag, so a messaging row reads as a message. */
function kindForTypeId(typeId: number): ScheduleKind {
    switch (typeId) {
        case SCHEDULE_TYPE.DATETIME:
            return 'oneoff';
        case SCHEDULE_TYPE.RECURRING:
            return 'recurring';
        case SCHEDULE_TYPE.STATION_CHANGE:
            return 'stationchange';
        case SCHEDULE_TYPE.INSERT:
            return 'insert';
        case SCHEDULE_TYPE.MESSAGING:
            return 'message';
        default:
            return 'unknown';
    }
}

function railColor(row: PlayerScheduleRow): string {
    return scheduleKindColor(kindForTypeId(row.custom_playlist_schedule_type_id), row.priority);
}

function missingDaysText(row: PlayerScheduleRow): string {
    return row.missing_day_ids.map((dayId) => DAY_TEXT_LABELS[dayId] ?? String(dayId)).join(' ');
}

function openList(row: PlayerScheduleRow): void {
    openRecord('LocationZoneCustomPlaylist', row.location_zone_custom_playlist_id);
}

/** A custom list file opens the list on its Tracks; a daily file opens the generated playlist. */
function openFile(file: PlayerScheduleFile): void {
    void openPlaylist(file.file_name, { customListSection: 'tracks' });
}

function trackCountText(file: PlayerScheduleFile): string {
    if (file.missing_track_count > 0) {
        return `${file.missing_track_count} of ${file.total_track_count} tracks missing`;
    }
    return `${file.total_track_count} ${file.total_track_count === 1 ? 'track' : 'tracks'}`;
}

const otherFilesOpen = ref(false);
const otherFiles = computed(() => schedule.value?.custom.other_files ?? []);

// ---------------------------------------------------------------------------
// Daily lists
// ---------------------------------------------------------------------------

const dailySummary = computed(() => (schedule.value ? dailyRangeSummary(schedule.value.daily) : null));
const dailyListOpen = ref(false);
/** The player's own date, because a daily list is for a day on its calendar. */
const todayDate = computed(() => schedule.value?.daily.today ?? dayjs(nowMs.value).format('YYYY-MM-DD'));

/** Each day from the first to the last: its files, or a missing row. */
const dailyRows = computed(() => {
    const daily = schedule.value?.daily;
    if (!daily) {
        return [];
    }
    const rows: Array<{ key: string; date: string; file: PlayerScheduleFile | null }> = daily.files.map((entry, index) => ({
        key: `file-${index}-${entry.file.file_name}`,
        date: entry.date,
        file: entry.file,
    }));
    for (const date of daily.missing_dates) {
        rows.push({ key: `missing-${date}`, date, file: null });
    }
    return rows.sort((left, right) => left.date.localeCompare(right.date));
});
</script>

<template>
    <div class="flex flex-col gap-4" data-testid="lzp-schedule">
        <p v-if="scheduleQuery.isLoading.value" class="text-muted-foreground text-xs">Loading…</p>
        <div
            v-else-if="scheduleQuery.isError.value"
            class="border-border/80 bg-muted/20 rounded-lg border px-3 py-2 text-xs"
            data-testid="lzp-schedule-error"
        >
            <p class="text-destructive">Couldn't load the player's lists.</p>
            <p v-if="errorMessage" class="text-muted-foreground mt-1">{{ errorMessage }}</p>
        </div>
        <template v-else-if="schedule && statusLine">
            <!-- The status line. Clicking it opens and closes the reasons. -->
            <div class="border-border/80 bg-muted/20 rounded-lg border px-3 py-2">
                <button
                    type="button"
                    class="focus-visible:ring-ring flex w-full items-center justify-between gap-2 text-left text-sm outline-none focus-visible:ring-1"
                    :aria-expanded="reasonsOpen"
                    data-testid="lzp-schedule-status"
                    @click="reasonsOpen = !reasonsOpen"
                >
                    <span class="min-w-0 truncate">{{ statusLine.updatedText }}</span>
                    <span class="flex shrink-0 items-center gap-1.5 text-xs font-semibold" data-testid="lzp-schedule-badge">
                        <span v-if="statusLine.badge === 'out_of_date'" class="size-2 rounded-full bg-amber-500" aria-hidden="true" />
                        <CheckIcon v-else-if="statusLine.badge === 'up_to_date'" class="text-muted-foreground size-3.5" aria-hidden="true" />
                        <ArrowPathIcon v-else-if="statusLine.badge === 'updating'" class="text-muted-foreground size-3.5 animate-spin" aria-hidden="true" />
                        {{ statusLine.badgeLabel }}
                        <ChevronDownIcon
                            class="text-muted-foreground size-3.5 transition-transform duration-200"
                            :class="reasonsOpen ? 'rotate-180' : ''"
                            aria-hidden="true"
                        />
                    </span>
                </button>
                <div v-if="reasonsOpen" class="mt-2 flex flex-col gap-2 pb-1 text-xs" data-testid="lzp-schedule-reasons">
                    <div>
                        <p class="text-muted-foreground font-medium">Custom lists</p>
                        <p v-if="schedule.custom.state === 'unknown'" class="mt-0.5">
                            Can't tell. The player's report has no time to compare the changes with.
                        </p>
                        <template v-else>
                            <p v-for="change in schedule.custom.reasons" :key="change.id" class="mt-0.5">
                                {{ describeChange(change) }}
                                <span class="text-muted-foreground">· {{ formatSyncTime(change.created_at, nowMs) }}</span>
                            </p>
                            <p v-if="schedule.custom.hidden_reason_count > 0" class="text-muted-foreground mt-0.5">
                                {{ schedule.custom.hidden_reason_count }} more
                                {{ schedule.custom.hidden_reason_count === 1 ? 'change' : 'changes' }} on a master zone you
                                can't see
                            </p>
                            <p
                                v-if="schedule.custom.reasons.length === 0 && schedule.custom.hidden_reason_count === 0"
                                class="text-muted-foreground mt-0.5"
                            >
                                No changes since the player's report.
                            </p>
                        </template>
                    </div>
                    <div>
                        <p class="text-muted-foreground font-medium">Daily lists</p>
                        <p v-if="schedule.daily.state === 'unknown'" class="text-muted-foreground mt-0.5">
                            No daily lists on the player.
                        </p>
                        <template v-else>
                            <p v-for="reason in schedule.daily.reasons" :key="`${reason.kind}-${reason.name}-${reason.updated_at}`" class="mt-0.5">
                                {{ dailyReasonText(reason, nowMs) }}
                            </p>
                            <p v-if="schedule.daily.reasons.length === 0" class="text-muted-foreground mt-0.5">
                                No template changes since the newest daily list was generated.
                            </p>
                        </template>
                    </div>
                </div>
            </div>

            <!-- Custom list schedules -->
            <section
                class="border-border/80 bg-muted/20 flex flex-col gap-2 rounded-lg border px-3 py-2"
                data-testid="lzp-schedule-custom"
            >
                <div class="flex items-center justify-between gap-2">
                    <h4 class="text-sm font-semibold">Custom list schedules</h4>
                    <LazyTooltip v-if="schedule.can_issue_commands">
                        <Button
                            type="button"
                            variant="toolbar"
                            size="sm"
                            class="aria-disabled:opacity-50 aria-disabled:hover:bg-transparent px-2"
                            :aria-label="isReloading ? 'Reloading custom lists…' : 'Reload custom lists'"
                            :aria-disabled="isReloading || undefined"
                            data-testid="lzp-schedule-reload"
                            @click="requestReload"
                        >
                            <ArrowPathIcon class="size-4 shrink-0" :class="isReloading ? 'animate-spin' : ''" />
                        </Button>
                        <template #content>{{ isReloading ? 'Reloading custom lists…' : 'Reload custom lists' }}</template>
                    </LazyTooltip>
                </div>
                <LzpScheduleProgressBar v-if="progress && progress.kind === 'reload'" :progress="progress" />
                <p v-if="actionNotice?.kind === 'reload'" class="text-destructive text-xs" data-testid="lzp-schedule-notice">
                    {{ actionNotice.text }}
                </p>

                <p v-if="schedule.custom.schedules.length === 0" class="text-muted-foreground text-xs">
                    No custom list schedules for this player.
                </p>
                <ul v-else class="flex flex-col gap-1.5">
                    <li
                        v-for="row in schedule.custom.schedules"
                        :key="row.location_zone_custom_playlist_schedule_id"
                        class="bg-background/80 border-border/60 flex gap-2 rounded border py-1.5 pr-2 pl-1.5"
                        data-testid="lzp-schedule-row"
                    >
                        <span class="w-1 shrink-0 self-stretch rounded-full" :style="{ backgroundColor: railColor(row) }" aria-hidden="true" />
                        <div class="flex min-w-0 flex-1 flex-col gap-0.5">
                            <div class="flex min-w-0 items-center gap-1.5 text-sm">
                                <ScheduleTypeIcon :kind="kindForTypeId(row.custom_playlist_schedule_type_id)" />
                                <button
                                    v-if="row.list_name !== null"
                                    type="button"
                                    class="focus-visible:ring-ring truncate rounded font-medium hover:underline focus-visible:outline-none focus-visible:ring-1"
                                    data-testid="lzp-schedule-list-name"
                                    @click="openList(row)"
                                >
                                    {{ row.list_name }}
                                </button>
                                <span v-else class="text-muted-foreground truncate italic">From master zone</span>
                            </div>
                            <p class="text-muted-foreground text-xs">{{ scheduleWhenText(row) }}</p>
                            <div
                                v-for="file in row.files"
                                :key="file.file_name"
                                class="flex min-w-0 items-center gap-1.5 text-[11px]"
                            >
                                <CheckIcon class="size-3 shrink-0 text-green-600 dark:text-green-500" aria-label="On the player" />
                                <button
                                    type="button"
                                    class="focus-visible:ring-ring min-w-0 truncate rounded font-mono hover:underline focus-visible:outline-none focus-visible:ring-1"
                                    :title="file.file_name"
                                    data-testid="lzp-schedule-file"
                                    @click="openFile(file)"
                                >
                                    {{ file.file_name }}
                                </button>
                                <span
                                    class="shrink-0 tabular-nums"
                                    :class="file.missing_track_count > 0 ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground'"
                                >
                                    {{ trackCountText(file) }}
                                </span>
                            </div>
                            <p v-if="!row.on_player" class="flex items-center gap-1.5 text-xs" data-testid="lzp-schedule-missing">
                                <span class="text-destructive font-semibold" aria-hidden="true">–</span>
                                <span>
                                    Not on the player yet<template v-if="row.missing_day_ids.length > 0">
                                        · no file for {{ missingDaysText(row) }}</template>
                                </span>
                            </p>
                        </div>
                    </li>
                </ul>

                <div v-if="otherFiles.length > 0">
                    <button
                        type="button"
                        class="text-muted-foreground hover:text-foreground focus-visible:ring-ring flex items-center gap-1 rounded text-xs focus-visible:outline-none focus-visible:ring-1"
                        :aria-expanded="otherFilesOpen"
                        data-testid="lzp-schedule-other-files"
                        @click="otherFilesOpen = !otherFilesOpen"
                    >
                        {{ otherFiles.length }} other {{ otherFiles.length === 1 ? 'file' : 'files' }}
                        <ChevronDownIcon class="size-3 transition-transform duration-200" :class="otherFilesOpen ? 'rotate-180' : ''" aria-hidden="true" />
                    </button>
                    <ul v-if="otherFilesOpen" class="mt-1 flex flex-col gap-0.5">
                        <li v-for="file in otherFiles" :key="file.file_name" class="flex min-w-0 items-center gap-1.5 text-[11px]">
                            <button
                                type="button"
                                class="focus-visible:ring-ring min-w-0 truncate rounded font-mono hover:underline focus-visible:outline-none focus-visible:ring-1"
                                :title="file.file_name"
                                data-testid="lzp-schedule-other-file"
                                @click="openFile(file)"
                            >
                                {{ file.file_name }}
                            </button>
                            <span class="text-muted-foreground shrink-0 tabular-nums">{{ trackCountText(file) }}</span>
                        </li>
                    </ul>
                </div>
            </section>

            <!-- Daily lists -->
            <section
                class="border-border/80 bg-muted/20 flex flex-col gap-2 rounded-lg border px-3 py-2"
                data-testid="lzp-schedule-daily"
            >
                <div class="flex items-center justify-between gap-2">
                    <h4 class="text-sm font-semibold">Daily lists</h4>
                    <LazyTooltip v-if="schedule.can_generate">
                        <Button
                            type="button"
                            variant="toolbar"
                            size="sm"
                            class="aria-disabled:opacity-50 aria-disabled:hover:bg-transparent px-2"
                            :aria-label="isGenerating ? 'Generating default lists…' : 'Generate and load default lists'"
                            :aria-disabled="isGenerating || undefined"
                            data-testid="lzp-schedule-generate"
                            @click="requestGenerate"
                        >
                            <ArrowPathIcon class="size-4 shrink-0" :class="isGenerating ? 'animate-spin' : ''" />
                        </Button>
                        <template #content>
                            {{ isGenerating ? 'Generating default lists…' : 'Generate and load default lists' }}
                        </template>
                    </LazyTooltip>
                </div>
                <LzpScheduleProgressBar v-if="progress && progress.kind === 'generate'" :progress="progress" />

                <div v-if="dailySummary" class="text-sm" data-testid="lzp-schedule-daily-summary">
                    <p v-if="dailySummary.range" class="font-medium">{{ dailySummary.range }}</p>
                    <p class="text-muted-foreground text-xs">{{ dailySummary.detail }}</p>
                </div>
                <div v-if="schedule.daily.files.length > 0">
                    <button
                        type="button"
                        class="text-muted-foreground hover:text-foreground focus-visible:ring-ring flex items-center gap-1 rounded text-xs focus-visible:outline-none focus-visible:ring-1"
                        :aria-expanded="dailyListOpen"
                        data-testid="lzp-schedule-daily-toggle"
                        @click="dailyListOpen = !dailyListOpen"
                    >
                        {{ dailyListOpen ? 'Hide' : `Show all ${schedule.daily.files.length}` }}
                        <ChevronDownIcon class="size-3 transition-transform duration-200" :class="dailyListOpen ? 'rotate-180' : ''" aria-hidden="true" />
                    </button>
                    <ul v-if="dailyListOpen" class="mt-1 flex flex-col gap-0.5">
                        <li
                            v-for="dailyRow in dailyRows"
                            :key="dailyRow.key"
                            class="flex min-w-0 items-center gap-2 text-[11px]"
                            :class="dailyRow.date === todayDate ? 'font-semibold' : ''"
                            :data-today="dailyRow.date === todayDate || undefined"
                            data-testid="lzp-schedule-daily-row"
                        >
                            <span class="w-16 shrink-0 tabular-nums">{{ formatDailyDate(dailyRow.date) }}</span>
                            <button
                                v-if="dailyRow.file"
                                type="button"
                                class="focus-visible:ring-ring min-w-0 truncate rounded font-mono hover:underline focus-visible:outline-none focus-visible:ring-1"
                                :title="dailyRow.file.file_name"
                                data-testid="lzp-schedule-daily-file"
                                @click="openFile(dailyRow.file)"
                            >
                                {{ dailyRow.file.file_name }}
                            </button>
                            <span v-else class="text-destructive">No list for this day</span>
                        </li>
                    </ul>
                </div>
            </section>
        </template>

        <ConfirmDialog
            v-model:open="confirmReloadOpen"
            title="Reload custom lists?"
            message="The player deletes its custom list files and downloads them again. This takes a few minutes, and the page updates as it goes."
            confirm-text="Reload"
            @confirm="reload"
        />
        <ConfirmDialog
            v-model:open="confirmGenerateOpen"
            title="Generate and load default lists?"
            :message="generateMessage"
            confirm-text="Generate and load"
            @confirm="generate"
        />
    </div>
</template>
